/**
 * Description: Unified system editor page for both creating and editing systems
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import SystemForm from '@components/SystemForm'
import { api, System } from '@services/api'
import { toast } from 'react-toastify'

const SystemEditor: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  const [system, setSystem] = useState<System | null>(null)
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isEditMode) {
      loadSystem()
    }
  }, [id])

  const loadSystem = async () => {
    if (!id) return

    try {
      setIsLoading(true)
      const data = await api.getSystemById(parseInt(id))
      setSystem(data)
    } catch (error) {
      toast.error('Failed to load system')
      // eslint-disable-next-line no-console
      console.error(error)
      navigate('/systems')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (data: System) => {
    try {
      setIsSaving(true)

      if (isEditMode && id) {
        // Update existing system
        await api.updateSystem(parseInt(id), data)
        toast.success('System updated successfully!')
        navigate(`/systems/${id}`)
      } else {
        // Create new system
        await api.createSystem(data)
        toast.success('System created successfully!')
        navigate('/systems')
      }
    } catch (error: any) {
      // Extract validation error messages if available
      let errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} system`

      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        // Format validation errors
        const validationErrors = error.response.data.details.map((err: any) => `${err.message}`).join('; ')
        errorMessage = validationErrors
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      }

      toast.error(errorMessage)
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (isEditMode && id) {
      navigate(`/systems/${id}`)
    } else {
      navigate('/systems')
    }
  }

  if (isLoading) {
    return (
      <div className="card py-12 text-center">
        <div className="border-primary-600 inline-block h-12 w-12 animate-spin rounded-full border-b-2"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Loading system...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={handleCancel}
          className="cursor-pointer rounded-lg p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {isEditMode ? 'Edit System' : 'Create New System'}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isEditMode ? system?.system_name : 'Add a new system to the inventory'}
          </p>
        </div>
      </div>

      <div className="card">
        <SystemForm system={system || undefined} onSubmit={handleSubmit} onCancel={handleCancel} isLoading={isSaving} />
      </div>
    </div>
  )
}

export default SystemEditor
